import React, { useCallback, useState, useRef, useEffect } from "react";
import { UserIcon, Volume2, VolumeX, RotateCcw, Copy, Check } from "lucide-react";
import { cn } from '@flowstate/utils';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ExtendedMessage } from "../types/chat";
import { useAuth } from '@/hooks/useAuth';
import { Button } from "@/components/ui/button";
import { getInitials } from "@/utils/userUtils";
import { TooltipWrapper } from '@/components/ui/tooltip-wrapper';
import { ChatVisualContent } from './ChatVisualContent';
import { ChatTodoList } from './ChatTodoList';
import { TaskExtractionLoading } from './TaskExtractionLoading';
import { AccountCreationPrompt } from './AccountCreationPrompt';
import { EnhancedMarkdownRenderer } from './EnhancedMarkdownRenderer';
import { CortexThinkingAnimation } from './CortexThinkingAnimation';
import { HealthPlanBlock } from './HealthPlanBlock';
import { MechaResultBlock } from './MechaResultBlock';
import { StreamingText } from './StreamingText';
import { WorkTrail, workTrailFromMeta } from './WorkTrail';
import { ToolActivityTimeline } from './ToolActivityTimeline';
import { agentBranding } from '../config/agentBranding';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { logger } from "@/lib/logger";

interface ChatMessageProps {
  message: ExtendedMessage;
  isError?: boolean;
  showBooking?: boolean;
  /** True when this is the last message in the list — enables typewriter reveal fallback. */
  isLatest?: boolean;
  onReplay?: (message: ExtendedMessage) => void;
  /** Called when user clicks a suggested follow-up question; receives the question text. */
  onFollowUpClick?: (text: string) => void;
}

// Typing indicator component
const TypingIndicator = () => (
  <div className="flex space-x-1 py-2">
    <div className="typing-dot animate-bounce"></div>
    <div className="typing-dot animate-bounce delay-150"></div>
    <div className="typing-dot animate-bounce delay-300"></div>
  </div>
);

const MIN_THINKING_DISPLAY_MS = 500; // Always show thinking frame at least this long; then "Loaded" when content arrives

const ChatMessage = ({ message, isError = false, showBooking: _showBooking = false, isLatest = false, onReplay, onFollowUpClick }: ChatMessageProps) => {
  const { user, profile } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentUtterance, setCurrentUtterance] = useState<SpeechSynthesisUtterance | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showLoadedPhase, setShowLoadedPhase] = useState(false);
  const thinkingShownAtRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  // Track mounted state to prevent memory leaks
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Typewriter fallback: when a new assistant response lands without streaming
  // (SSE events arrived in a burst), animate the text reveal on mount.
  const [typewriterActive, setTypewriterActive] = useState(() =>
    isLatest && message.role === 'assistant' && !message.isTyping
    && typeof message.content === 'string' && message.content.length > 0
    && message.id !== 'initial-message'
  );

  const { role, content, isTyping, visualContent, todoList, showAccountPrompt, orchestratorMeta, followUpQuestions, toolActivity } = message;
  const workTrailProps = workTrailFromMeta({
    ...orchestratorMeta,
    toolActivity: toolActivity ?? orchestratorMeta?.toolActivity,
  });

  // Defensive: ensure content is always a renderable string
  const safeContent = typeof content === 'string'
    ? content
    : (content != null ? JSON.stringify(content) : '');

  const isThinking = role === 'assistant' && !safeContent && (orchestratorMeta?.isThinking || isTyping);
  if (isThinking && thinkingShownAtRef.current == null) {
    thinkingShownAtRef.current = Date.now();
  }
  if (!isThinking && !showLoadedPhase) {
    thinkingShownAtRef.current = null;
  }

  // When content arrives after thinking, show "Loaded" for the remainder of MIN_THINKING_DISPLAY_MS
  useEffect(() => {
    if (role !== 'assistant' || !safeContent || thinkingShownAtRef.current == null) {
      return;
    }
    const elapsed = Date.now() - thinkingShownAtRef.current;
    if (elapsed >= MIN_THINKING_DISPLAY_MS) {
      thinkingShownAtRef.current = null;
      return;
    }
    setShowLoadedPhase(true);
    const remainder = MIN_THINKING_DISPLAY_MS - elapsed;
    const t = setTimeout(() => {
      setShowLoadedPhase(false);
      thinkingShownAtRef.current = null;
    }, remainder);
    return () => clearTimeout(t);
  }, [role, safeContent]);

  const showThinkingFrame = isThinking || (role === 'assistant' && showLoadedPhase);

  // Detect if message suggests booking a consultation
  const _isBookingMessage = safeContent.toLowerCase().includes("book") ||
    safeContent.toLowerCase().includes("consultation") ||
    safeContent.toLowerCase().includes("schedule");

  // Text-to-speech functionality for Cortex responses
  const handleSpeakMessage = useCallback(() => {
    if (isPlaying && currentUtterance) {
      // Stop current speech
      speechSynthesis.cancel();
      setIsPlaying(false);
      setCurrentUtterance(null);
      return;
    }

    // Check if browser supports speech synthesis
    if (!('speechSynthesis' in window)) {
      logger.warn("Speech synthesis not supported in this browser");
      return;
    }

    // Create new utterance
    const utterance = new SpeechSynthesisUtterance(safeContent);

    // Configure voice settings
    utterance.rate = 0.9; // Slightly slower for clarity
    utterance.pitch = 1.0;
    utterance.volume = 0.8;

    // Try to use a more natural voice if available
    const voices = speechSynthesis.getVoices();
    const preferredVoices = voices.filter(voice =>
      voice.lang.startsWith('en') &&
      (voice.name.includes('Neural') || voice.name.includes('Premium') || voice.name.includes('Enhanced'))
    );

    if (preferredVoices.length > 0) {
      utterance.voice = preferredVoices[0];
    } else {
      // Fallback to any English voice
      const englishVoices = voices.filter(voice => voice.lang.startsWith('en'));
      if (englishVoices.length > 0) {
        utterance.voice = englishVoices[0];
      }
    }

    // Event listeners
    utterance.onstart = () => {
      setIsPlaying(true);
      logger.info(" Cortex is speaking");
    };

    utterance.onend = () => {
      setIsPlaying(false);
      setCurrentUtterance(null);
      logger.info(" Cortex finished speaking");
    };

    utterance.onerror = (event) => {
      logger.error("Speech synthesis error:", { error: event.error });
      setIsPlaying(false);
      setCurrentUtterance(null);
    };

    setCurrentUtterance(utterance);
    speechSynthesis.speak(utterance);
  }, [safeContent, isPlaying, currentUtterance]);

  // Copy message content
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(safeContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error('Failed to copy:', { error: error });
    }
  }, [safeContent]);

  // Replay message
  const handleReplay = useCallback(() => {
    if (onReplay) {
      onReplay(message);
    }
  }, [message, onReplay]);

  return (
    <div
      data-message-role={role}
      className={cn(
        "flex gap-3 max-w-full group",
        role === "user" ? "justify-end" : "justify-start"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Show thinking animation when assistant is thinking or in brief "Loaded" phase so frame always appears */}
      {role === "assistant" && showThinkingFrame ? (
        <CortexThinkingAnimation 
          orchestratorMeta={showLoadedPhase ? { ...orchestratorMeta, isThinking: true } : orchestratorMeta}
          statusMessage={showLoadedPhase ? 'Loaded' : orchestratorMeta?.statusMessage}
          compact={false}
        />
      ) : (
        <>
          {role === "assistant" && (
            <div className="flex flex-col items-center gap-1">
              <Avatar className="h-8 w-8 bg-blue-600 text-white">
                <AvatarImage src={agentBranding.avatarSrc} alt={`${agentBranding.name} Assistant`} />
                <AvatarFallback className="bg-blue-600 text-white flex items-center justify-center">
                  {agentBranding.avatarFallback}
                </AvatarFallback>
              </Avatar>
              {/* Voice output button for assistant messages */}
              {!isTyping && (
                <TooltipWrapper
                  content={isPlaying ? `Stop ${agentBranding.name} speaking` : `Have ${agentBranding.name} speak this message`}
                  side="right"
                  priority="high"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSpeakMessage}
                    className={`h-6 w-6 p-0 transition-all duration-200 ${isPlaying
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                      : 'text-muted-foreground hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                      }`}
                  >
                    {isPlaying ? (
                      <VolumeX className="h-3 w-3" />
                    ) : (
                      <Volume2 className="h-3 w-3" />
                    )}
                  </Button>
                </TooltipWrapper>
              )}
            </div>
          )}

          <div
            className={cn(
              "rounded-2xl max-w-[90%]",
              role === "user"
                ? "bg-primary text-primary-foreground px-4 py-3"
                : isError
                  ? "bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-2xl"
                  : "bg-transparent px-5 py-4"
            )}
          >

        {isTyping && !safeContent ? (
          <TypingIndicator />
        ) : (
          <>
            {/* Collapsible assistant text with enhanced markdown formatting (Cascade style) */}
            {role === "assistant" ? (
              <>
                {/* Structured specialist data — renders instantly before LLM commentary */}
                {message.specialistData && (
                  <div className="mb-3">
                    {message.specialistData.tool.startsWith('mecha-') ? (
                      <MechaResultBlock
                        tool={message.specialistData.tool}
                        data={message.specialistData.structuredData}
                        mechaShift={message.specialistData.mechaShift}
                      />
                    ) : (
                      <HealthPlanBlock tool={message.specialistData.tool} data={message.specialistData.structuredData} />
                    )}
                  </div>
                )}
                <div className={cn("relative", !expanded && "max-h-[480px] overflow-hidden pr-2")}>
                  <StreamingText
                    text={safeContent}
                    isStreaming={!!isTyping}
                    animateOnMount={typewriterActive}
                    onRevealComplete={() => {
                      if (isMountedRef.current) {
                        setTypewriterActive(false);
                      }
                    }}
                    charsPerFrame={8}
                  >
                    {(visibleText) => <EnhancedMarkdownRenderer content={visibleText} />}
                  </StreamingText>
                  {/* Streaming / typewriter cursor — glowing line for Neo-mode feel */}
                  {(isTyping || typewriterActive) && safeContent && (
                    <span className="inline-block w-[2px] h-[1.1em] bg-primary ml-0.5 align-text-bottom animate-cursor-glow" />
                  )}
                  {!expanded && safeContent && safeContent.length > 1200 && (
                    <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-muted to-transparent" />
                  )}
                </div>
                {safeContent && safeContent.length > 1200 && !isTyping && (
                  <div className="mt-2 flex justify-center">
                    <Button variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)}>
                      {expanded ? "Show less" : "Show more"}
                    </Button>
                  </div>
                )}
                {/* Live tool trail while answer is still streaming */}
                {isTyping && (toolActivity ?? orchestratorMeta?.toolActivity)?.entries?.length ? (
                  <div className="mt-3">
                    <ToolActivityTimeline
                      activity={(toolActivity ?? orchestratorMeta?.toolActivity)!}
                    />
                  </div>
                ) : null}
              </>
            ) : (
              <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-1">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    code: ({ className: _className, children, ...props }) => (
                      <code className="bg-primary-foreground/10 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                        {children}
                      </code>
                    ),
                    p: ({ children }) => <span className="leading-relaxed">{children}</span>,
                  }}
                >
                  {safeContent}
                </ReactMarkdown>
              </div>
            )}

            {/* Enhanced Visual Content */}
            {visualContent && (
              <ChatVisualContent content={visualContent} />
            )}

            {/* Inline Action Items - shown directly in message when detected */}
            {todoList && todoList.items.length > 0 && !isTyping && (
              <div className="mt-3 pt-3 border-t border-border/30">
                <ChatTodoList
                  todoList={todoList}
                  messageId={message.id}
                />
              </div>
            )}

            {/* Durable work trail — steps + tools after the answer */}
            {role === 'assistant' && !isTyping && workTrailProps && (
              <WorkTrail {...workTrailProps} />
            )}

            {/* Task Extraction Loading — Framer Motion skeleton while AI extracts action items */}
            {role === 'assistant' && !isTyping && !todoList && (
              <TaskExtractionLoading isExtracting={!!message.isExtractingTasks} />
            )}

            {/* Account Creation Prompt */}
            {showAccountPrompt && !user && (
              <AccountCreationPrompt />
            )}

            {/* Suggested follow-up questions */}
            {role === 'assistant' && !isTyping && followUpQuestions && followUpQuestions.length > 0 && onFollowUpClick && (
              <div className="mt-3 pt-3 border-t border-border/30">
                <p className="text-xs text-muted-foreground mb-2">Suggested follow-ups</p>
                <div className="flex flex-wrap gap-2">
                  {followUpQuestions.map((q, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => onFollowUpClick(q)}
                      className={cn(
                        "text-xs px-3 py-1.5 rounded-full border border-border/50 bg-muted/30",
                        "hover:bg-primary/15 hover:border-primary/40 hover:text-primary transition-colors"
                      )}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Message Actions - Gold Standard (ChatGPT/Claude style) */}
            {isHovered && !isTyping && (
              <div className="flex items-center gap-1.5 mt-3 pt-2.5 border-t border-border/30">
                {role === "user" && onReplay && (
                  <TooltipWrapper content="Replay this message" side="top">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleReplay}
                      className="h-8 px-3 text-xs gap-1.5 hover:bg-accent/80 transition-all duration-200 rounded-lg"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      <span className="font-medium">Replay</span>
                    </Button>
                  </TooltipWrapper>
                )}
                <TooltipWrapper content={copied ? "Copied!" : "Copy message"} side="top">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    className="h-8 px-3 text-xs gap-1.5 hover:bg-accent/80 transition-all duration-200 rounded-lg"
                  >
                    {copied ? (
                      <><Check className="h-3 w-3 text-green-500" /> Copied</>
                    ) : (
                      <><Copy className="h-3 w-3" /> Copy</>
                    )}
                  </Button>
                </TooltipWrapper>
              </div>
            )}
          </>
        )}

        {/* Booking interface removed - Quick Actions panel provides all needed functionality */}
          </div>

          {role === "user" && (
            <Avatar className="h-8 w-8 mt-0.5">
              {user && user.id ? (
                <>
                  <AvatarImage src={profile?.avatar_url || undefined} />
                  <AvatarFallback>
                    {getInitials(profile?.full_name ?? user?.email)}
                  </AvatarFallback>
                </>
              ) : (
                <AvatarFallback>
                  <UserIcon className="h-5 w-5" />
                </AvatarFallback>
              )}
            </Avatar>
          )}
        </>
      )}
    </div>
  );
};

export default ChatMessage;